import React, { useMemo } from 'react';
import { CardEntity } from './types';
import SimpleListView from '../../ClientCard/SimpleListView/SimpleListView';
import { UserDefinedObjectTypeId } from 'App/resources/Model/UserDefinedObjectType';
import BemClass from '@upsales/components/Utils/bemClass';
import UserDefinedObjectAttributes from 'App/babel/attributes/UserDefinedObjectAttributes';
import { useSelector } from 'react-redux';
import { RootState } from 'Store/index';
import UserDefinedObjectResource, { UserDefinedObject } from 'App/resources/UserDefinedObject';
import RequestBuilder from 'Resources/RequestBuilder';
import { useCustomFields } from 'App/components/hooks/appHooks';
import ComparisonTypes from 'Resources/ComparisonTypes';
import { Headline, Icon, Row } from '@upsales/components';
import { removeItem } from 'Store/helpers/array';
import logError from 'Helpers/logError';
import { PrimaryButton } from '@upsales/components/Buttons';
import { useTranslation } from 'Components/Helpers/translate';
import openModal from 'App/services/Modal';

const getUDOs = (cardEntity: CardEntity, typeId: number, rb: RequestBuilder) => {
	if (cardEntity.type === 'client') {
		rb.addFilter(UserDefinedObjectAttributes.client.attr.id, ComparisonTypes.Equals, cardEntity.id);
	} else {
		rb.addFilter(UserDefinedObjectAttributes.contact.attr.id, ComparisonTypes.Equals, cardEntity.id);
	}
	const query = rb.build();
	return UserDefinedObjectResource.find(typeId as UserDefinedObjectTypeId, query);
};

const SharedUDO = ({ cardEntity, typeId }: { cardEntity: CardEntity; typeId: number }) => {
	const udoMetadata = useSelector(({ App }: RootState) =>
		App.metadata?.params.UserDefinedObject.find(udo => udo.id === typeId)
	);
	const { t } = useTranslation();

	const cacheKey = `userDefined${typeId}` as 'userDefined1' | 'userDefined2' | 'userDefined3' | 'userDefined4';
	const customFields = useCustomFields(cacheKey);

	const className = 'ClientCardUDO';

	const columns = useMemo(() => {
		const fields: { id: number }[] = customFields
			.filter(field => field.$hasAccess)
			.filter(field => field.visible)
			.sort((a, b) => (a.sortId < b.sortId ? -1 : 1));
		return fields.map(f => `Custom_${f.id}`).concat(['delete']);
	}, [typeId]);

	if (!udoMetadata || !cardEntity) {
		return null;
	}

	return (
		<SimpleListView<UserDefinedObject>
			classes={new BemClass(className)}
			getData={rb => getUDOs(cardEntity, typeId, rb)}
			renderHeader={({ reloadTable }) => (
				<Row align="center" className="SimpleListView__header">
					<Headline space="plm" size="sm">
						{udoMetadata.name}
					</Headline>
					<div className="FlexPadder" />
					<PrimaryButton
						onClick={() => {
							openModal('EditUDO', {
								typeId: typeId as UserDefinedObjectTypeId,
								object: cardEntity.type === 'client' ? { client: cardEntity.data } : { contact: cardEntity.data },
								onClose: () => reloadTable({ silent: true, debounce: true })
							});
						}}
						size="sm"
					>
						<Icon name="plus" space="mrs" />
						{`${t('default.add')} ${t('default.objects')}`}
					</PrimaryButton>
				</Row>
			)}
			columns={columns.length > 0 ? columns : ['', 'delete']}
			attributes={UserDefinedObjectAttributes}
			listType={`userDefinedObject${typeId}`}
			onRowClick={(item, provided) => () => {
				openModal('EditUDO', {
					id: item.id,
					typeId: typeId as UserDefinedObjectTypeId,
					onClose: () => provided.reloadTable({ silent: true })
				});
			}}
			deleteFn={async (item, { getTableData, setTableData }) => {
				try {
					await UserDefinedObjectResource.delete(typeId as UserDefinedObjectTypeId, item.id);

					const data = getTableData();
					const index = data.findIndex(userDefinedObject => userDefinedObject.id === item.id);

					if (index !== -1) {
						const final = removeItem(data, index);
						setTableData(final);
					}
				} catch (error) {
					logError(error, 'Failed to delete udo');
				}
			}}
			entityName={'default.userdefinedobject'}
			isDeletable={item => item.userRemovable}
			broadcastType={`userDefinedObject${typeId}`}
		/>
	);
};

export default SharedUDO;
